/**
 * Starting a long server endpoint and polling it to the end.
 *
 * Builds and deliveries take minutes, and the CDN in front of the API gives up
 * on a request after one — the browser then sees a 504 with no CORS headers on
 * it, which reads as an opaque CORS failure while the server is still happily
 * building. So those endpoints answer 202 with a job id and keep working; this
 * polls a status endpoint and hands back exactly the `{ ok, status, data | error }`
 * the caller would have received from a request that had waited, so nothing
 * above this module has to know a job was involved.
 *
 * The registry behind it is one (controller/snapshotEditor/jobs.js) but the
 * status endpoint is per router — a snapshot editor base and a versionedPlayEngine
 * base are authorized differently — so `statusPath` is a parameter and the
 * snapshot editor's is only the default.
 */

import _ from "lodash";
import { getServerUri } from "./uriUtility";

export const DEFAULT_JOB_STATUS_PATH = "snapshotEditor/jobStatus";

/**
 * @param {object} [options]
 * @param {number} [options.timeoutMs]  abort the request after this long. Set
 *   only by the repeated, tiny calls (job polling): a snapshot save posts
 *   megabytes and is allowed to take as long as it takes. Without it a
 *   half-open connection — the socket the laptop had before it slept — hangs
 *   the caller until the browser's own multi-minute limit expires.
 */
export async function postJson(servicePath, payload, { timeoutMs } = {}) {
  const controller =
    timeoutMs && typeof AbortController !== "undefined" ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;

  try {
    const response = await fetch(getServerUri(servicePath), {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({
        accessToken: localStorage.getItem("session.accessToken"),
        ...payload,
      }),
      ...(controller ? { signal: controller.signal } : {}),
    });

    // 202 as well as 200: the long endpoints answer with a job id and finish in
    // the background (see runJob).
    if (response.ok) {
      return { ok: true, status: response.status, data: await response.json() };
    }

    // Errors come back as a bare code string (see the controllers); a conflict
    // also carries the current file so the caller can recover without a re-fetch.
    const contentType = response.headers.get("content-type") || "";
    const body = contentType.includes("application/json")
      ? await response.json()
      : await response.text();

    return { ok: false, status: response.status, error: body };
  } finally {
    if (timer) clearTimeout(timer);
  }
}

// Tight at first, because a small task can be done in seconds; slower once it
// is clear this is a long one, so a half-hour matrix is not a thousand requests.
const JOB_POLL_MS = 2000;
const JOB_POLL_SLOW_MS = 5000;
const JOB_POLL_SLOW_AFTER = 15;

/**
 * How long polling keeps trying while requests are failing, measured from the
 * FIRST failure rather than counted in attempts.
 *
 * The work does not live in the request: the server keeps a finished job for
 * thirty minutes (controller/snapshotEditor/jobs.js) and the agent container
 * runs a delivery to completion whether or not anyone is still listening. So
 * giving up early does not stop anything — it only throws away a result that
 * exists, and leaves the operator to find out from Drive and Asana whether the
 * files went out.
 *
 * It used to be five attempts, which sounds forgiving and is not: a delivery
 * polls every five seconds, so five attempts was twenty-five seconds — less
 * than a laptop takes to wake its Wi-Fi up. Three minutes covers that, a VPN
 * switch, and an origin restart. What a long budget must NOT do is look like a
 * hang, which is what onConnectionState is for.
 */
const JOB_FAILURE_BUDGET_MS = 3 * 60 * 1000;
/** Backoff between failed polls, so the budget is ~20 requests, not ~100. */
const JOB_RETRY_MS = [2000, 5000, 10000, 15000, 15000];
/** A poll that cannot be answered in this long is a lost poll, not a wait. */
const JOB_POLL_TIMEOUT_MS = 30 * 1000;

/** Polling gave up: nothing answered for the whole budget. The job lives on. */
export const JOB_CONNECTION_LOST = "CONNECTION_LOST";
/** The server answered that the job is gone — it restarted, or it isn't ours. */
export const JOB_LOST = "JOB_LOST";
/** The opening request never landed, so nothing was started. */
export const JOB_NOT_STARTED = "NOT_STARTED";

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Start a long endpoint, then poll it until it is done.
 *
 * `onProgress` is called with the events reported so far, whenever there are new
 * ones — it is the only thing that makes the wait legible.
 *
 * `onConnectionState` is called with `{ down: true, sinceMs }` on every failed
 * poll and once with `{ down: false }` when polling recovers. Without it a lost
 * connection is indistinguishable from a slow step: the progress line simply
 * stops moving, for as long as the failure budget lasts.
 *
 * `statusPath` is the router's own status endpoint; the job id is only valid
 * there, because that is where the caller's access to `payload.baseID` is
 * checked.
 */
export async function runJob(
  servicePath,
  payload,
  { onProgress, onConnectionState } = {},
  { statusPath = DEFAULT_JOB_STATUS_PATH } = {}
) {
  const jobType = servicePath.split("/").pop();

  let started;
  try {
    started = await postJson(servicePath, payload);
  } catch (_err) {
    // Nothing was started. (Strictly: the 202 could have been lost on its way
    // back, in which case the job did start — the container's per-task lock is
    // what stops a second attempt from running alongside the first.)
    return { ok: false, status: 0, error: JOB_NOT_STARTED, jobType };
  }

  // Refused before the work began — a validation error, no permission — or a
  // server old enough to answer with the result directly.
  if (!started.ok) return started;
  const jobId = _.get(started, "data.jobId");
  if (!jobId) return started;

  return followJob(
    { baseID: payload.baseID, jobId, jobType, statusPath },
    { onProgress, onConnectionState }
  );
}

/**
 * Wait on a job that is already running — the half of runJob that does not
 * start anything.
 *
 * Separate because the browser is not the only way into a job's lifetime: the
 * work runs in the agent container and the server keeps the job, so a dialog
 * that was closed, or a page that was reloaded, re-attaches to what it finds in
 * `listJobs` and reaches exactly the same answer it would have waited for.
 */
export async function followJob(
  { baseID, jobId, jobType, statusPath = DEFAULT_JOB_STATUS_PATH },
  { onProgress, onConnectionState } = {}
) {
  // "The server answered, and its answer is that this job is gone" is final;
  // "nothing answered" is not. A 404 or 403 minted by the CDN carries neither
  // of these codes, so it is treated as a lost poll and retried.
  const isJobGone = (poll) => {
    const code = _.get(poll, "error.error") || _.get(poll, "error");
    return code === "JOB_NOT_FOUND" || code === "JOB_BELONGS_TO_ANOTHER_USER";
  };

  let reported = 0;
  let failingSince = null;
  let retryIndex = -1;

  /** @returns true when the failure budget is spent. */
  const noteFailure = () => {
    if (!failingSince) failingSince = Date.now();
    retryIndex = Math.min(retryIndex + 1, JOB_RETRY_MS.length - 1);
    if (onConnectionState) onConnectionState({ down: true, sinceMs: Date.now() - failingSince });
    return Date.now() - failingSince > JOB_FAILURE_BUDGET_MS;
  };
  const connectionLost = () => ({
    ok: false,
    status: 0,
    error: JOB_CONNECTION_LOST,
    jobId,
    jobType,
  });

  for (let polls = 0; ; polls += 1) {
    await delay(
      failingSince
        ? JOB_RETRY_MS[retryIndex]
        : polls < JOB_POLL_SLOW_AFTER
          ? JOB_POLL_MS
          : JOB_POLL_SLOW_MS
    );

    let poll;
    try {
      poll = await postJson(statusPath, { baseID, jobId }, { timeoutMs: JOB_POLL_TIMEOUT_MS });
    } catch (_err) {
      if (noteFailure()) return connectionLost();
      continue;
    }

    if (!poll.ok) {
      // Waiting cannot bring back a job the server no longer has, nor mint a
      // token it has already expired, nor fix a request its own validator
      // refused (the express-validator `errors` array is only ever ours).
      if (isJobGone(poll)) return { ok: false, status: poll.status, error: JOB_LOST, jobId, jobType };
      if (poll.status === 401) return poll;
      if (poll.status === 400 && _.get(poll, "error.errors")) return poll;
      if (noteFailure()) return connectionLost();
      continue;
    }

    if (failingSince) {
      failingSince = null;
      retryIndex = -1;
      if (onConnectionState) onConnectionState({ down: false });
    }

    const progress = _.get(poll, "data.progress", []);
    if (onProgress && progress.length > reported) {
      reported = progress.length;
      onProgress(progress);
    }

    if (poll.data.state !== "finished") continue;

    // The controller's own response, replayed.
    const { status, body } = poll.data;
    return status >= 200 && status < 300
      ? { ok: true, status, data: body }
      : { ok: false, status, error: body };
  }
}
